import { useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '../lib/cn'
import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'
import { errorMessage } from '../lib/api'
import { Icon } from '../components/ui/Icon'
import { Input } from '../components/ui/Input'
import { Popover } from '../components/ui/Popover'
import { Skeleton } from '../components/ui/Skeleton'
import { Button } from '../components/ui/Button'
import { BrandAvatar } from '../components/domain/BrandAvatar'
import { NetworkGlyph } from '../components/domain/NetworkGlyph'

/**
 * Picks the brand every other screen is scoped to. Search appears once there are more than
 * eight, because an agency on the 50-brand plan cannot scan a list.
 */
export const BrandSwitcher = (): ReactElement => {
  const t = useT()
  const { brands, brandsStatus, brandsError, reloadBrands, activeBrand, switchBrand } = useAuth()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return brands
    return brands.filter((brand) => brand.name.toLowerCase().includes(needle))
  }, [brands, query])

  const close = (): void => {
    setOpen(false)
    setQuery('')
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((previous) => !previous)
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="ring-focus flex h-9 min-w-0 items-center gap-2 rounded-sm px-2 text-chrome-ink transition-colors hover:bg-chrome-700"
      >
        {brandsStatus === 'pending' ? (
          <Skeleton width="7rem" height={14} />
        ) : activeBrand ? (
          <>
            <BrandAvatar brand={activeBrand} size="xs" surface="chrome" />
            <span className="t-small max-w-40 truncate font-semibold">{activeBrand.name}</span>
          </>
        ) : (
          <span className="t-small text-chrome-mute">{t('shell.brandSwitcher.none')}</span>
        )}
        <Icon name="chevron-down" size={14} className="text-chrome-mute" />
      </button>

      <Popover
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        placement="bottom"
        align="start"
        surface="chrome"
        label={t('common.shell.brandSwitcher')}
        className="w-[min(20rem,calc(100vw-1rem))] p-2"
      >
        {brands.length > 8 ? (
          <div className="mb-2">
            <Input
              tone="chrome"
              inputSize="sm"
              value={query}
              placeholder={t('shell.brandSwitcher.find')}
              aria-label={t('shell.brandSwitcher.find')}
              prefix={<Icon name="search" size={14} />}
              onChange={(event) => {
                setQuery(event.target.value)
              }}
            />
          </div>
        ) : null}

        {brandsStatus === 'pending' ? (
          <div className="flex flex-col gap-2 p-2">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} height={20} />
            ))}
          </div>
        ) : brandsStatus === 'error' ? (
          <div className="flex flex-col items-start gap-2 p-3">
            <p className="t-small text-chrome-ink">{t('shell.brandSwitcher.loadFailed')}</p>
            <p className="t-small text-chrome-mute">{errorMessage(brandsError)}</p>
            <Button size="sm" variant="secondary" onClick={reloadBrands}>
              {t('common.actions.retry')}
            </Button>
          </div>
        ) : brands.length === 0 ? (
          <div className="flex flex-col items-start gap-2 p-3">
            <p className="t-small text-chrome-ink">{t('shell.brandSwitcher.emptyTitle')}</p>
            <p className="t-small text-chrome-mute">{t('shell.brandSwitcher.emptyBody')}</p>
            <Link
              to="/app/brands"
              onClick={close}
              className="t-small ring-focus font-semibold text-brass underline underline-offset-2"
            >
              {t('shell.brandSwitcher.addFirst')}
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <p className="t-small p-3 text-chrome-mute">
            {t('shell.brandSwitcher.noMatch', { query })}
          </p>
        ) : (
          <ul className="scroll-chrome max-h-80 overflow-y-auto">
            {filtered.map((brand) => {
              const active = brand.blogId === activeBrand?.blogId
              return (
                <li key={brand.id}>
                  <button
                    type="button"
                    onClick={() => {
                      switchBrand(brand.blogId)
                      close()
                    }}
                    className={cn(
                      'ring-focus-inset flex w-full items-center gap-2.5 rounded-sm px-2 py-2 text-left transition-colors',
                      active ? 'bg-chrome-600' : 'hover:bg-chrome-600',
                    )}
                  >
                    <BrandAvatar brand={brand} size="sm" surface="chrome" />
                    <span className="min-w-0 flex-1">
                      <span className="t-small block truncate text-chrome-ink">{brand.name}</span>
                      <span className="t-mono block text-chrome-mute">{brand.timezone}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      {brand.connections.slice(0, 5).map((connection) => (
                        <NetworkGlyph
                          key={connection.id}
                          provider={connection.provider}
                          size={13}
                          surface="chrome"
                        />
                      ))}
                    </span>
                    {active ? <Icon name="check" size={14} className="text-brass" /> : null}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </Popover>
    </>
  )
}
